import { inject, Injectable } from '@angular/core';

import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { LoggerService } from '@ph-core/services/logger/logger.service';

const TRACE_STORAGE_KEY = 'davinci.auth-flow-trace';
const TRACE_MAX_ENTRIES = 80;

export type AuthFlowScenario =
  | 'unknown'
  | 'deep-link-home'
  | 'deep-link-callback'
  | 'guard-on-home-route'
  | 'guard-blocked-home'
  | 'callback-orchestration'
  | 'callback-navigate-to-content'
  | 'post-ssologin-navigation'
  | 'token-renewal-scheduler'
  | 'is-authenticated-check'
  | 'login-guard'
  | 'password-reset';

export interface AuthFlowTraceEntry {
  traceId: string;
  step: number;
  at: string;
  atMs: number;
  scenario: AuthFlowScenario;
  event: string;
  locationHref: string;
  routerUrl: string | null;
  detail?: Record<string, unknown>;
}

/**
 * Ordered trace of auth decisions for reproducing redirect loops / password-reset locally.
 * Enable with `localStorage.setItem('davinci.logger', 'auth,user-store,router')`.
 * Dump history: `JSON.parse(sessionStorage.getItem('davinci.auth-flow-trace'))`
 * Or in console after auth logs fire: `window.__davinciAuthDebug()`
 */
@Injectable({ providedIn: 'root' })
export class AuthFlowTraceService {
  private readonly logger = inject(LoggerService);
  private readonly traceId = this._createTraceId();
  private step = 0;

  record(
    scenario: AuthFlowScenario,
    event: string,
    detail?: Record<string, unknown>,
    routerUrl?: string | null
  ): void {
    if (!this.logger.isContextEnabled(LoggerContext.Auth)) {
      return;
    }

    this.step += 1;
    const entry: AuthFlowTraceEntry = {
      traceId: this.traceId,
      step: this.step,
      at: new Date().toISOString(),
      atMs: Date.now(),
      scenario,
      event,
      locationHref: typeof window !== 'undefined' ? window.location.href : '',
      routerUrl: routerUrl ?? null,
      ...(detail ? { detail } : {}),
    };

    this.logger.log(LoggerContext.Auth, '[auth-flow-trace]', entry);
    this._appendToSessionHistory(entry);
    this._exposeDebugHelpersOnce();
  }

  dumpHistory(): AuthFlowTraceEntry[] {
    try {
      const raw = sessionStorage.getItem(TRACE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthFlowTraceEntry[]) : [];
    } catch {
      return [];
    }
  }

  getTraceId(): string {
    return this.traceId;
  }

  private _createTraceId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return `trace-${Date.now()}`;
  }

  private _exposeDebugHelpersOnce(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const win = window as Window & {
      __davinciAuthDebug?: () => { traceId: string; history: AuthFlowTraceEntry[] };
    };
    if (win.__davinciAuthDebug) {
      return;
    }
    win.__davinciAuthDebug = () => ({
      traceId: this.traceId,
      history: this.dumpHistory(),
    });
  }

  private _appendToSessionHistory(entry: AuthFlowTraceEntry): void {
    try {
      const raw = sessionStorage.getItem(TRACE_STORAGE_KEY);
      const history: AuthFlowTraceEntry[] = raw ? (JSON.parse(raw) as AuthFlowTraceEntry[]) : [];
      history.push(entry);
      const trimmed = history.length > TRACE_MAX_ENTRIES ? history.slice(-TRACE_MAX_ENTRIES) : history;
      sessionStorage.setItem(TRACE_STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
      // Ignore storage failures in trace helper.
    }
  }
}
